import {isValidImageCoordinates} from '../../../../../Map/imageCoordinates'

const collectCoordinatePairs = (value, pairs = []) => {
  if (!Array.isArray(value)) {
    return pairs
  }

  if (
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  ) {
    pairs.push(value)
    return pairs
  }

  value.forEach((item) => collectCoordinatePairs(item, pairs))
  return pairs
}

const getBboxCoordinatesFromPolygon = (polygon) => {
  const coordinates = collectCoordinatePairs(polygon?.coordinates || polygon)

  if (coordinates.length < 1) {
    return null
  }

  const lngValues = coordinates.map((coordinate) => coordinate[0])
  const latValues = coordinates.map((coordinate) => coordinate[1])
  const minLng = Math.min(...lngValues)
  const maxLng = Math.max(...lngValues)
  const minLat = Math.min(...latValues)
  const maxLat = Math.max(...latValues)

  if (![minLng, maxLng, minLat, maxLat].every(Number.isFinite)) {
    return null
  }

  const bbox = [
    [minLng, maxLat],
    [maxLng, maxLat],
    [maxLng, minLat],
    [minLng, minLat],
  ]
  return isValidImageCoordinates(bbox) ? bbox : null
}

export const getLayerBbox = (headers, polygon) => {
  if (headers?.['x-bbox']) {
    try {
      const bbox = JSON.parse(headers['x-bbox'])
      if (isValidImageCoordinates(bbox)) {
        return bbox
      }
    } catch (error) {
      // Fallback below.
    }
  }

  return getBboxCoordinatesFromPolygon(polygon)
}

export const recoverCachedImageLayer = (payload, polygon) => {
  if (!payload) {
    return null
  }
  const bbox = isValidImageCoordinates(payload.bbox)
    ? payload.bbox
    : getBboxCoordinatesFromPolygon(polygon)
  return bbox ? {...payload, bbox} : null
}

export const parseIndexRangeHeader = (headers) => {
  const serializedIntervals = headers?.['x-index-range'] ?? headers?.['x-intervals']

  if (!serializedIntervals) {
    return null
  }

  try {
    const intervals = JSON.parse(serializedIntervals)
    return Array.isArray(intervals) && intervals.every((item) => item && typeof item === 'object' && !Array.isArray(item))
      ? intervals : null
  } catch (error) {
    return null
  }
}

// Single-raster headers use fractions; legend UI uses percentages. Preserve
// provider colors, open interval bounds and measured AOI metadata unchanged.
export const parseSingleIndexLegendHeaders = (headers) => {
  const intervals = parseIndexRangeHeader(headers)
  if (!intervals?.length) return parseImageQuality(headers) ? {...headers} : null
  return {
    ...headers,
    'x-index-range': JSON.stringify(intervals.map((interval) => ({
      ...interval,
      percentage: interval.percentage == null || interval.percentage === ''
        ? null : Number.isFinite(Number(interval.percentage)) ? Number(interval.percentage) * 100 : null,
    }))),
  }
}

// Do not confuse scene-wide cloud statistics with measured quality of this lot.
export const parseImageQuality = (headers) => {
  try {
    const quality = JSON.parse(headers?.['x-image-quality'] || 'null')
    if (!quality || !['udm2_aoi', 'sentinel_aoi'].includes(quality.source)) return null
    const conditions = [
      ['cloud_fraction', 'cloud'], ['shadow_fraction', 'shadow'],
      ['light_haze_fraction', 'haze'], ['heavy_haze_fraction', 'heavy_haze'],
      ['snow_fraction', 'snow'],
    ].flatMap(([key, label]) => {
      const value = quality[key]
      return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1
        ? [{label, percentage: value * 100}] : []
    })
    const displayCoverage = headers?.['x-display-coverage-fraction'] != null
      ? Number(headers['x-display-coverage-fraction']) : quality.coverage_fraction
    if (typeof displayCoverage === 'number' && Number.isFinite(displayCoverage) &&
        displayCoverage >= 0 && displayCoverage < 1) {
      conditions.push({label: 'missing', percentage: (1 - displayCoverage) * 100})
    }
    if (!conditions.length && typeof quality.valid_fraction === 'number' &&
        Number.isFinite(quality.valid_fraction) && quality.valid_fraction >= 0 && quality.valid_fraction < 1) {
      conditions.push({label: 'unreliable', percentage: (1 - quality.valid_fraction) * 100})
    }
    return conditions.length ? {conditions, statisticsPolicy: headers?.['x-statistics-policy']} : null
  } catch (error) {
    return null
  }
}
